<script>
import HeaderWithTitleAndIcon from "@/components/HeaderWithTitleAndIcon.vue";
import {getAdminBlogDetailApi, createBlogApi, updateBlogApi, deleteBlogApi} from "@/api/adminBlogApi";

export default {
  name: 'AdminBlogEditView',
  components: {HeaderWithTitleAndIcon},
  data () {
    return {
      title: '',
      content: '',
      status: 'draft',
      tagsInput: '',
      loading: false,
      submitting: false
    }
  },
  computed: {
    blogId () {
      return this.$route.params.id
    },
    isEditMode () {
      return !!this.blogId
    }
  },
  created () {
    if (this.isEditMode) {
      this.fetchBlog()
    }
  },
  methods: {
    async fetchBlog () {
      this.loading = true
      try {
        const res = await getAdminBlogDetailApi(this.blogId)
        this.title = res.data.title
        this.content = res.data.content
        this.status = res.data.status
        this.tagsInput = (res.data.tags || []).join(', ')
      } catch (error) {
        if (error.code === 401 || error.code === 403) {
          await this.$router.push('/admin/login')
        } else if (error.code === 404) {
          await this.$router.push('/admin')
        }
      } finally {
        this.loading = false
      }
    },
    parseTags () {
      return this.tagsInput
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0)
    },
    async handleSubmit () {
      if (!this.title || !this.content) {
        return
      }
      this.submitting = true
      const payload = {
        title: this.title,
        content: this.content,
        status: this.status,
        tags: this.parseTags()
      }
      try {
        if (this.isEditMode) {
          await updateBlogApi(this.blogId, payload)
        } else {
          await createBlogApi(payload)
        }
        await this.$router.push('/admin')
      } catch (error) {
        if (error.code === 401 || error.code === 403) {
          await this.$router.push('/admin/login')
        }
      } finally {
        this.submitting = false
      }
    },
    async handleDelete () {
      if (!window.confirm(`確定要刪除《${this.title}》嗎？此動作無法復原。`)) {
        return
      }
      try {
        await deleteBlogApi(this.blogId)
        await this.$router.push('/admin')
      } catch (error) {
        if (error.code === 401 || error.code === 403) {
          await this.$router.push('/admin/login')
        }
      }
    }
  }
}
</script>

<template>
  <div class="admin-blog-edit">
    <header-with-title-and-icon>
      <template #header-title>
        {{ isEditMode ? 'Edit Post' : 'New Post' }}
      </template>
    </header-with-title-and-icon>

    <p v-if="loading">Loading...</p>
    <form v-else class="edit-form" @submit.prevent="handleSubmit">
      <label class="field">
        <span>Title</span>
        <input v-model="title" type="text" required>
      </label>

      <label class="field">
        <span>Status</span>
        <select v-model="status">
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </label>

      <label class="field">
        <span>Tags（用逗號分隔）</span>
        <input v-model="tagsInput" type="text" placeholder="生活, 旅行">
      </label>

      <label class="field">
        <span>Content</span>
        <textarea v-model="content" rows="16" required></textarea>
      </label>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="submitting">
          {{ submitting ? 'Saving...' : 'Save' }}
        </button>
        <router-link to="/admin" class="btn">Cancel</router-link>
        <button v-if="isEditMode" type="button" class="btn btn-danger" @click="handleDelete">Delete</button>
      </div>
    </form>
  </div>
</template>

<style scoped lang="less">
.admin-blog-edit {
  max-width: 700px;
  margin: 20px auto;
  padding: 0 20px;

  .edit-form {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .field {
      display: flex;
      flex-direction: column;
      gap: 6px;

      input, select, textarea {
        padding: 0.6rem;
        border: 1px solid var(--gray);
        border-radius: 3px;
        font-size: 1rem;
        font-family: inherit;
      }

      textarea {
        resize: vertical;
      }
    }

    .form-actions {
      display: flex;
      gap: 10px;
    }
  }

  .btn {
    display: inline-block;
    padding: 0.6rem 1.2rem;
    border: 1px solid var(--gray);
    border-radius: 3px;
    background: transparent;
    color: var(--gray);
    text-decoration: none;
    cursor: pointer;
    font-size: 1rem;

    &:hover:not(:disabled) {
      color: var(--blue);
      border-color: var(--blue);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .btn-primary {
    background-color: var(--blue);
    border-color: var(--blue);
    color: var(--white);

    &:hover:not(:disabled) {
      background-color: var(--blueHover);
      color: var(--white);
    }
  }

  .btn-danger {
    &:hover:not(:disabled) {
      color: var(--red);
      border-color: var(--red);
    }
  }
}
</style>
